import { useEffect, useState } from "react";
import { Handshake, Phone, Menu, X } from "lucide-react";
import logo from "@/assets/logo.jpg";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";

interface HeaderProps {
  onAction: (action: string) => void;
}

const navLinks = [
  { href: "/#products", label: "Продукция" },
  { href: "/#how", label: "Как мы работаем" },
  { href: "/#why-us", label: "О нас" },
  { href: "/#faq", label: "FAQ" },
];

const Header = ({ onAction }: HeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleQuote = () => {
    trackEvent("click_header_cta");
    setMobileOpen(false);
    onAction("price");
  };

  return (
    <header
      className={`sticky top-0 z-40 bg-background/95 backdrop-blur-md transition-shadow duration-200 ${
        scrolled ? "shadow-[0_2px_16px_-4px_hsla(220,20%,12%,0.10)] border-b border-border/60" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-6 px-4 md:px-8 lg:px-12 h-[72px] md:h-[80px]">
        {/* Logo */}
        <a href="/" className="flex items-center gap-3 shrink-0">
          <img
            src={logo}
            alt="Zebra Jaluzi"
            className="h-11 w-11 md:h-14 md:w-14 rounded-full object-cover"
          />
          <div className="leading-tight">
            <div className="text-[15px] md:text-[17px] font-bold text-foreground tracking-tight">Zebra Jaluzi</div>
            <div className="text-[10px] md:text-[11px] text-muted-foreground mt-0.5">Жалюзи и москитные сетки — Ташкент</div>
          </div>
        </a>

        {/* Center nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="relative text-sm font-medium text-foreground/75 hover:text-foreground transition-colors after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-primary after:transition-all hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          <Button asChild variant="outline" className="border-primary/35 text-primary hover:border-primary hover:bg-primary/5">
            <a href="/dileram" onClick={() => trackEvent("click_dealers_link")}>
              <Handshake className="h-4 w-4" />
              Дилерам
            </a>
          </Button>
          <a
            href="tel:+998781480001"
            onClick={() => trackEvent("click_call")}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/25 transition-all hover:scale-[1.03] hover:shadow-primary/40"
          >
            <Phone className="h-4 w-4" />
            +998 78 148 00 01
          </a>
          <button
            onClick={handleQuote}
            className="inline-flex items-center justify-center rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground transition-all hover:border-primary/50 hover:text-primary"
          >
            Узнать стоимость
          </button>
        </div>

        {/* Mobile burger */}
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg text-foreground hover:bg-muted transition-colors"
          aria-label="Меню"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border/60 bg-background/98 backdrop-blur-md animate-fade-in">
          <nav className="flex flex-col px-4 py-4 gap-1">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                className="px-3 py-3 rounded-lg text-base font-medium text-foreground/80 hover:bg-muted hover:text-foreground transition-colors"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/dileram"
              onClick={() => {
                trackEvent("click_dealers_link");
                setMobileOpen(false);
              }}
              className="flex items-center gap-2 px-3 py-3 rounded-lg font-semibold text-primary bg-primary/5"
            >
              <Handshake className="h-4 w-4" />
              Дилерам и партнёрам
            </a>
            <div className="flex flex-col gap-2 pt-3 mt-2 border-t border-border/60">
              <a
                href="tel:+998781480001"
                onClick={() => trackEvent("click_call")}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/25"
              >
                <Phone className="h-4 w-4" />
                +998 78 148 00 01
              </a>
              <button
                onClick={handleQuote}
                className="inline-flex items-center justify-center rounded-xl border border-border bg-card px-4 py-3.5 text-sm font-semibold text-foreground"
              >
                Узнать стоимость
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
